import { OAUTH_TOKEN_URLS } from "./destinationConstants";

/**
 * Per-type config builders. Each function returns ONLY the keys that apply
 * to its destination type — we deliberately drop stale form fields so the
 * DB is not polluted when the user switches type mid-form.
 *
 * Adding a new destination type = add another entry here. `buildConfig`
 * itself is a one-liner dispatcher.
 */
/**
 * Iter-61.3 — every destination-config key that holds a credential and
 * therefore gets replaced with the bullet-string sentinel by
 * `backend/routes/delivery_routes.py::_redact_secret` before the API
 * returns it. Keep this list in sync with the backend redaction list.
 */
export const SECRET_KEYS = [
  "secret", "auth_token", "password", "secret_access_key", "api_key",
  "api_token", "oauth_client_secret", "oauth_refresh_token",
];

/**
 * Iter-61.3 — the backend replaces every credential with "••••••••" in
 * the list/get response. When the admin clicks Edit we DO NOT want to
 * seed the form with that redacted placeholder — a subsequent
 * `buildConfig(form)` write would overwrite the real stored value with
 * bullets. We therefore blank every secret key on edit-load. The
 * input's placeholder tells the admin "leave blank to keep existing"
 * (implemented in the field components). If the admin explicitly types
 * a new secret it survives to the PATCH payload.
 */
export function blankSecretsForEdit(cfg) {
  const out = { ...(cfg || {}) };
  for (const k of SECRET_KEYS) if (k in out) out[k] = "";
  return out;
}

/**
 * Iter-61.3 — after `buildConfig(form)` produces the PATCH `config`,
 * drop any secret key whose value is empty / whitespace / all-bullet.
 * Combined with the backend defensive strip in
 * `update_destination`, this guarantees an unchanged secret survives
 * an Edit round-trip.
 */
export function stripBlankSecretsForPatch(cfg) {
  const out = { ...(cfg || {}) };
  for (const k of SECRET_KEYS) {
    const v = out[k];
    if (v === undefined) continue;
    if (typeof v !== "string") continue;
    const trimmed = v.trim();
    if (trimmed === "" || /^•+$/.test(trimmed)) delete out[k];
  }
  return out;
}

/**
 * Iter-62.1 — Shared destination config fields (Geographic Filter).
 * Iter-62.1.1 — Buyer Default must EXPLICITLY clear the stored override.
 *
 * `state_allowlist` + `state_fees` are rendered by the shared
 * `StateAllowlistFields` component for EVERY destination type (email,
 * webhook, all 8 CRMs, Zapier, S3, FTP).
 *
 * Buyer Default vs Override is inferred from the existing form shape
 * (`config.state_allowlist` non-empty ⇒ override) — we deliberately
 * do NOT introduce a `state_allowlist_mode` field.
 *
 * Iter-62.1.1: the previous return of `{}` on Buyer Default was safe
 * for CREATE (empty destination has no override) but silently failed
 * on EDIT. Backend PATCH does `merged_cfg = { ...stored, ...patch_cfg }`
 * so a missing key means "leave stored value alone" — a stored
 * `state_allowlist=["AR","TX"]` therefore survived a
 * switch-to-Buyer-Default save. The fix: emit the empty
 * clearing shape explicitly. `_normalize_state_allowlist([])` and
 * `_normalize_state_fees({})` both return non-None on the backend
 * (per `backend/routes/delivery_routes.py`), so the PATCH merge writes
 * the empty values into stored config and the override is cleared.
 *
 * The Override branch also always emits `state_fees` (possibly `{}`)
 * so removing every per-state fee while keeping the states selected
 * still clears stored fees deterministically.
 */
function buildSharedDestinationConfig(cfg) {
  if (!cfg) return { state_allowlist: [], state_fees: {} };
  // Normalize `state_allowlist` — accepts either the array shape written
  // by `StateAllowlistFields` after user interaction OR the legacy
  // comma-separated string kept in place for backwards-compat.
  const raw = cfg.state_allowlist;
  const rawTrimmed = raw && String(raw).trim();
  const allowlist = rawTrimmed
    ? String(raw)
        .split(",")
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean)
    : [];
  // Iter-62.1.1 — Buyer Default: emit both empty values so a PATCH
  // clears whatever the destination previously had. Backend accepts
  // `state_allowlist: []` and `state_fees: {}` and writes them
  // through the merge, replacing any stored override. On CREATE the
  // same shape lands as "no destination-level state restriction"
  // which is exactly what Buyer Default means.
  if (!allowlist.length) return { state_allowlist: [], state_fees: {} };
  // Override — serialize the selected allowlist and any per-state fees.
  // Per-state fees ("AR:350, TX:400" → {AR: 350, TX: 400}). The shared
  // `StateAllowlistFields` field writes exclusively to
  // `state_fees_text`; the object shape only appears on edit-hydration
  // and is converted to text by the parent form's `edit()` handler
  // before the field remounts. Always emit the `state_fees` key —
  // possibly `{}` — so clearing the fees text while keeping the states
  // clears stored fees deterministically.
  const fees = {};
  const feesText = cfg.state_fees_text;
  if (feesText && String(feesText).trim()) {
    String(feesText).split(",").forEach((pair) => {
      const parts = pair.split(":").map((s) => s.trim());
      if (parts.length !== 2 || !parts[0]) return;
      const state = parts[0].toUpperCase();
      const fee = parseFloat(parts[1]);
      if (!Number.isNaN(fee) && fee >= 0) fees[state] = fee;
    });
  }
  return { state_allowlist: allowlist, state_fees: fees };
}

const CONFIG_BUILDERS = {
  email: (c) => ({
    to: c.to,
    subject: c.subject || undefined,
    template: c.template || undefined,
    reply_to: c.reply_to || undefined,
    daily_digest_enabled: !!c.daily_digest_enabled,
    daily_digest_recipient: c.daily_digest_recipient || undefined,
    // Iter-30: Payment mode + invoicing fields
    payment_mode: c.payment_mode || "prepay",
    invoice_enabled: !!c.invoice_enabled,
    invoice_recipient: c.invoice_recipient || undefined,
    // Iter-62.1: state_allowlist + state_fees now come from the shared
    // `buildSharedDestinationConfig` merge in `buildConfig` below so
    // every destination type — not just email — serializes them.
  }),

  webhook: (c) => ({
    url: c.url,
    method: c.method || "POST",
    secret: c.secret || undefined,
    reject_words: (c.reject_words || "").trim() || undefined,
  }),

  crm_zoho: (c) => ({
    endpoint: c.endpoint || undefined,
    module: c.module || "Leads",
    zoho_region: c.zoho_region || "com",
    default_company: c.default_company || undefined,
    oauth_mode: true,
    oauth_client_id: c.oauth_client_id,
    oauth_client_secret: c.oauth_client_secret,
    oauth_refresh_token: c.oauth_refresh_token,
    oauth_token_url:
      c.oauth_token_url ||
      `https://accounts.zoho.${c.zoho_region || "com"}/oauth/v2/token`,
  }),

  crm_pipedrive: (c) => ({
    endpoint: c.endpoint || undefined,
    api_token: c.api_token,
    owner_id: c.owner_id || undefined,
  }),

  crm_zapier: (c) => ({ url: c.url }), // catch-hook URL is the credential

  crm_lawruler: (c) => ({
    endpoint: c.endpoint,
    api_key: c.api_key,
    lead_provider: c.lead_provider || undefined,
    content_type: c.content_type || undefined,
  }),

  crm_leaddocket: (c) => ({
    endpoint: c.endpoint,
    content_type: c.content_type || undefined,
  }),

  s3: (c) => ({
    bucket: c.bucket,
    region: c.region,
    access_key_id: c.access_key_id,
    secret_access_key: c.secret_access_key,
    prefix: c.prefix || undefined,
  }),

  ftp: (c) => ({
    host: c.host,
    port: Number(c.port || 21),
    username: c.username,
    password: c.password,
    directory: c.directory || undefined,
    use_tls: !!c.use_tls,
  }),
};

/**
 * The four bearer-CRM types share an identical config shape. Handle them
 * with one shared builder rather than duplicating four case blocks.
 */
const BEARER_CRMS = ["crm_salesforce", "crm_litify", "crm_lawmatics", "crm_hubspot"];

function buildBearerCrm(type, c) {
  const base = { endpoint: c.endpoint };
  if (!c.oauth_mode) return { ...base, auth_token: c.auth_token };
  return {
    ...base,
    oauth_mode: true,
    oauth_client_id: c.oauth_client_id,
    oauth_client_secret: c.oauth_client_secret,
    oauth_refresh_token: c.oauth_refresh_token,
    oauth_token_url: c.oauth_token_url || OAUTH_TOKEN_URLS[type],
  };
}

BEARER_CRMS.forEach((type) => {
  CONFIG_BUILDERS[type] = (c) => buildBearerCrm(type, c);
});

/**
 * Build the `config` payload the server expects for the currently-selected
 * destination type. See CONFIG_BUILDERS above for per-type shapes.
 *
 * Iter-62.1: the shared `state_allowlist` / `state_fees` fields — which
 * the `StateAllowlistFields` component renders for EVERY destination
 * type — are merged after the type-specific keys so they land in the
 * outgoing payload regardless of the selected type. Shared fields
 * take precedence on any accidental key collision (the type builders
 * above intentionally no longer emit these keys so a collision is
 * not possible in normal use, but this ordering is the defensive
 * default).
 */
export function buildConfig(form) {
  const builder = CONFIG_BUILDERS[form.type];
  const typeConfig = builder ? builder(form.config) : {};
  const shared = buildSharedDestinationConfig(form.config);
  return { ...typeConfig, ...shared };
}

/**
 * Prefer the most human-recognizable identifier in the destination's config
 * for the "Target" column in the destinations table.
 */
const TARGET_PICKERS = [
  (c) => c.url,
  (c) => c.to,
  (c) => c.endpoint,
  (c) => (c.bucket ? `s3://${c.bucket}/${c.prefix || ""}` : null),
  (c) => (c.host ? `${c.host}:${c.port || 21}` : null),
];

export function targetDisplay(row) {
  const c = row.config || {};
  for (const pick of TARGET_PICKERS) {
    const v = pick(c);
    if (v) return v;
  }
  return "—";
}
